/** Site-wide SEO constants and helpers for the Vite SPA. */

export const SITE_ORIGIN = 'https://app.dhikrsession.com'
export const SITE_NAME = 'Dhikr Session'
export const SITE_NAME_AR = 'جلسة الذكر'

/** Default OG/Twitter image (logo). Prefer a 1200×630 asset in public/ when available. */
export const DEFAULT_OG_IMAGE = `${SITE_ORIGIN}/logo-512.png`

export const DEFAULT_DESCRIPTION_AR =
  'تطبيق جلسة الذكر لاكتشاف جلسات الذكر وحلقات الذكر والدروس الدينية القريبة، مواقيت الصلاة، وأقرب جامع ومسجد ومصلى.'

export const DEFAULT_DESCRIPTION_EN =
  'Discover nearby dhikr sessions, prayer times, mosques, Islamic centers, and community gatherings with Dhikr Session.'

export const DEFAULT_KEYWORDS =
  'جلسات ذكر, جلسة ذكر, تطبيق ذكر, مواقيت الصلاة, أقرب جامع, أقرب مسجد, أقرب مصلى, حلقات ذكر, دروس دينية, dhikr sessions, dhikr app, prayer times, nearest mosque, nearby mosque, Islamic centers'

export function absoluteUrl(path = '/'): string {
  if (path.startsWith('http://') || path.startsWith('https://')) return path
  const normalized = path.startsWith('/') ? path : `/${path}`
  return `${SITE_ORIGIN}${normalized}`
}

/** Locale is client-side (no path prefix); hreflang entries share the same absolute URL. */
export function hreflangLinks(path = '/') {
  const href = absoluteUrl(path)
  // Cast: @unhead's Link union treats rel:"alternate" as feed-like and requires `type`.
  return [
    { key: 'hreflang-ar', rel: 'alternate', hreflang: 'ar', href },
    { key: 'hreflang-en', rel: 'alternate', hreflang: 'en', href },
    { key: 'hreflang-x-default', rel: 'alternate', hreflang: 'x-default', href },
  ] as Array<{ key: string; rel: string; hreflang: string; href: string }>
}

export function ogLocaleTags(locale: string): Array<{ property: string; content: string }> {
  const primary = locale === 'ar' ? 'ar_SA' : 'en_US'
  const alternate = locale === 'ar' ? 'en_US' : 'ar_SA'
  return [
    { property: 'og:locale', content: primary },
    { property: 'og:locale:alternate', content: alternate },
  ]
}

export function resolveOgImage(image?: string | null): string {
  if (!image?.trim()) return DEFAULT_OG_IMAGE
  return absoluteUrl(image.trim())
}

export function buildPageTitle(pageTitle: string, locale: string): string {
  const brand = locale === 'ar' ? `${SITE_NAME_AR} | ${SITE_NAME}` : SITE_NAME
  if (!pageTitle) return brand
  return `${pageTitle} | ${brand}`
}

export function injectJsonLd(id: string, data: Record<string, unknown> | Record<string, unknown>[]): void {
  if (typeof document === 'undefined') return
  let el = document.getElementById(id) as HTMLScriptElement | null
  if (!el) {
    el = document.createElement('script')
    el.id = id
    el.type = 'application/ld+json'
    document.head.appendChild(el)
  }
  el.textContent = JSON.stringify(data)
}

export function removeJsonLd(id: string): void {
  document.getElementById(id)?.remove()
}

export function placeJsonLd(opts: {
  name: string
  description?: string
  url: string
  latitude?: number
  longitude?: number
  address?: string
  image?: string
}): Record<string, unknown> {
  const place: Record<string, unknown> = {
    '@context': 'https://schema.org',
    '@type': 'Place',
    name: opts.name,
    url: opts.url,
  }
  if (opts.description) place.description = opts.description
  if (opts.image) place.image = resolveOgImage(opts.image)
  if (opts.address) {
    place.address = { '@type': 'PostalAddress', streetAddress: opts.address }
  }
  if (opts.latitude != null && opts.longitude != null) {
    place.geo = {
      '@type': 'GeoCoordinates',
      latitude: opts.latitude,
      longitude: opts.longitude,
    }
  }
  return place
}

export function eventJsonLd(opts: {
  name: string
  description?: string
  url: string
  startDate?: string
  endDate?: string
  locationName?: string
  latitude?: number
  longitude?: number
  image?: string
}): Record<string, unknown> {
  const event: Record<string, unknown> = {
    '@context': 'https://schema.org',
    '@type': 'Event',
    name: opts.name,
    url: opts.url,
    eventAttendanceMode: 'https://schema.org/OfflineEventAttendanceMode',
    eventStatus: 'https://schema.org/EventScheduled',
  }
  if (opts.description) event.description = opts.description
  if (opts.startDate) event.startDate = opts.startDate
  if (opts.endDate) event.endDate = opts.endDate
  if (opts.image) event.image = resolveOgImage(opts.image)
  if (opts.locationName || (opts.latitude != null && opts.longitude != null)) {
    const location: Record<string, unknown> = {
      '@type': 'Place',
      name: opts.locationName || opts.name,
    }
    if (opts.latitude != null && opts.longitude != null) {
      location.geo = {
        '@type': 'GeoCoordinates',
        latitude: opts.latitude,
        longitude: opts.longitude,
      }
    }
    event.location = location
  }
  return event
}
